import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Segmented } from '@/components/ui/Segmented';
import { useThemePalette } from '@/config/theme';
import type { StandingWithTeam } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeRound, localizeTeamName } from '@/lib/i18n/footballNames';

// Fixed column widths keep the header and every row aligned inside the shared
// horizontal scroller (the table is wider than a phone, 365Scores-style).
const COL = { pos: 42, team: 140, num: 30, pts: 38, form: 110 } as const;
const NUM_KEYS = ['played', 'won', 'drawn', 'lost', 'goalDiff'] as const;
type NumKey = (typeof NUM_KEYS)[number];

/** The three table densities (365-style): Short, Full, Form. */
type TableMode = 'short' | 'full' | 'form';
const MODE_NUMS: Record<TableMode, readonly NumKey[]> = {
  short: ['played', 'goalDiff'],
  full: NUM_KEYS,
  form: ['played'],
};
function numValue(row: StandingWithTeam, key: NumKey): number {
  switch (key) {
    case 'won':
      return row.won;
    case 'drawn':
      return row.drawn;
    case 'lost':
      return row.lost;
    case 'goalDiff':
      return row.gd;
    default:
      return row.played;
  }
}
const tableWidth = (numKeys: readonly NumKey[], showForm: boolean) =>
  COL.pos + COL.team + COL.num * numKeys.length + COL.pts + (showForm ? COL.form : 0);

type Venue = 'overall' | 'home' | 'away';
const VENUES: Venue[] = ['overall', 'home', 'away'];
const BLANK_VENUE = { played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0 };

/** Remap rows to a venue's stats + re-rank within each group (display-only). */
function applyVenue(rows: StandingWithTeam[], venue: Venue): StandingWithTeam[] {
  if (venue === 'overall') return rows;
  const mapped = rows.map((row) => {
    const v = (venue === 'home' ? row.home : row.away) ?? BLANK_VENUE;
    return {
      ...row,
      played: v.played,
      won: v.won,
      drawn: v.drawn,
      lost: v.lost,
      gf: v.gf,
      ga: v.ga,
      gd: v.gf - v.ga,
      points: v.points,
      status: 'same',
    };
  });
  const byGroup = new Map<string, StandingWithTeam[]>();
  for (const r of mapped) {
    const key = r.group_label ?? '';
    const bucket = byGroup.get(key);
    if (bucket) bucket.push(r);
    else byGroup.set(key, [r]);
  }
  const out: StandingWithTeam[] = [];
  for (const group of byGroup.values()) {
    group.sort((a, b) => b.points - a.points || b.gd - a.gd || b.won - a.won);
    group.forEach((r, i) => out.push({ ...r, rank: i + 1 }));
  }
  return out;
}

const HeaderRow = React.memo(function HeaderRow({
  numKeys,
  showForm,
}: {
  numKeys: readonly NumKey[];
  showForm: boolean;
}) {
  const { t } = useTranslation();
  return (
    <View className="flex-row items-center border-b border-line py-2">
      <AppText variant="micro" weight="medium" className="text-center" style={{ width: COL.pos }}>
        {t('leagues.pos')}
      </AppText>
      <AppText variant="micro" weight="medium" className="ps-2" style={{ width: COL.team }}>
        {t('leagues.team')}
      </AppText>
      {numKeys.map((key) => (
        <AppText
          key={key}
          variant="micro"
          weight="medium"
          tabular
          className="text-center"
          style={{ width: COL.num }}
        >
          {t(`leagues.${key}`)}
        </AppText>
      ))}
      <AppText
        variant="micro"
        weight="bold"
        tabular
        className="text-center text-ink"
        style={{ width: COL.pts }}
      >
        {t('leagues.points')}
      </AppText>
      {showForm ? (
        <AppText variant="micro" weight="medium" className="ps-1" style={{ width: COL.form }}>
          {t('leagues.form')}
        </AppText>
      ) : null}
    </View>
  );
});

/** Last-5 results as compact colour-coded chips (W win · D draw · L loss). */
function FormChips({ form }: { form: string | null }) {
  const letters = (form ?? '')
    .toUpperCase()
    .replace(/[^WDL]/g, '')
    .slice(-5)
    .split('');
  if (letters.length === 0) {
    return (
      <View className="flex-row ps-1" style={{ width: COL.form }}>
        <AppText variant="micro" className="text-inactive">
          -
        </AppText>
      </View>
    );
  }
  const bg = (l: string) => (l === 'W' ? 'bg-win/20' : l === 'L' ? 'bg-loss/20' : 'bg-draw/20');
  const fg = (l: string) => (l === 'W' ? 'text-win' : l === 'L' ? 'text-loss' : 'text-draw');
  return (
    <View className="flex-row items-center gap-1 ps-1" style={{ width: COL.form }}>
      {letters.map((letter, index) => (
        <View
          key={index}
          className={`h-[18px] w-[18px] items-center justify-center rounded ${bg(letter)}`}
        >
          <AppText
            variant="micro"
            weight="bold"
            className={fg(letter)}
            style={{ fontSize: 9, lineHeight: 12 }}
          >
            {letter}
          </AppText>
        </View>
      ))}
    </View>
  );
}

type Zone = 'qualify' | 'releg' | null;

/** Top-2 advance in a group stage; top-4 qualify / bottom-3 relegate in a league. */
function zoneFor(rank: number, size: number, grouped: boolean): Zone {
  if (grouped) return rank <= 2 ? 'qualify' : null;
  if (rank <= 4) return 'qualify';
  if (rank > size - 3) return 'releg';
  return null;
}

function LegendDot({ tone, label }: { tone: string; label: string }) {
  return (
    <View className="flex-row items-center gap-1.5">
      <View className={`h-2 w-2 rounded-full ${tone}`} />
      <AppText variant="micro" className="text-muted">
        {label}
      </AppText>
    </View>
  );
}

const StandingRowView = React.memo(function StandingRowView({
  row,
  highlight,
  zone,
  liveScore,
  numKeys,
  showForm,
}: {
  row: StandingWithTeam;
  highlight: boolean;
  zone: Zone;
  /** This team's current goals in a live match (its perspective), if playing now. */
  liveScore?: { gf: number; ga: number };
  numKeys: readonly NumKey[];
  showForm: boolean;
}) {
  const palette = useThemePalette();
  const router = useRouter();
  const { numeralOptions, locale, isRTL } = useLocale();
  const fmt = (value: number) => formatNumber(value, numeralOptions);
  const liveTone = liveScore
    ? liveScore.gf > liveScore.ga
      ? { bg: 'bg-win/20', text: 'text-win' }
      : liveScore.gf < liveScore.ga
        ? { bg: 'bg-loss/20', text: 'text-loss' }
        : { bg: 'bg-surface-2', text: 'text-soft' }
    : null;
  const rankColor = zone === 'qualify' ? 'text-win' : zone === 'releg' ? 'text-loss' : 'text-ink';
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => row.team && router.push(`/team/${row.team.id}`)}
      className={`flex-row items-center py-3 active:bg-surface-2/60 ${highlight ? 'bg-volt/10' : ''}`}
    >
      <View className="flex-row items-center justify-center gap-0.5" style={{ width: COL.pos }}>
        <AppText variant="micro" weight="bold" tabular className={rankColor}>
          {fmt(row.rank)}
        </AppText>
        {row.status === 'up' ? (
          <Ionicons name="caret-up" size={9} color={palette.win} />
        ) : row.status === 'down' ? (
          <Ionicons name="caret-down" size={9} color={palette.loss} />
        ) : null}
      </View>
      <View className="flex-row items-center gap-2 ps-2" style={{ width: COL.team }}>
        <TeamCrest uri={row.team?.logo_url} name={localizeTeamName(row.team, locale)} size="sm" />
        <AppText variant="caption" weight="medium" className="shrink text-ink" numberOfLines={1}>
          {localizeTeamName(row.team, locale, true)}
        </AppText>
        {liveScore && liveTone ? (
          <View className={`rounded-md px-1.5 py-0.5 ${liveTone.bg}`}>
            <AppText variant="micro" weight="bold" tabular className={liveTone.text}>
              {isRTL
                ? `⁦${fmt(liveScore.ga)}-${fmt(liveScore.gf)}⁩`
                : `⁦${fmt(liveScore.gf)}-${fmt(liveScore.ga)}⁩`}
            </AppText>
          </View>
        ) : null}
      </View>
      {numKeys.map((key) => (
        <AppText
          key={key}
          variant="micro"
          tabular
          className="text-center"
          style={{ width: COL.num }}
        >
          {fmt(numValue(row, key))}
        </AppText>
      ))}
      <AppText
        variant="micro"
        weight="bold"
        tabular
        className="text-center text-ink"
        style={{ width: COL.pts }}
      >
        {fmt(row.points)}
      </AppText>
      {showForm ? <FormChips form={row.form} /> : null}
    </Pressable>
  );
});

/** Standings, split into group cards when the competition has groups. */
export function StandingsTable({
  rows,
  highlightTeamIds,
  isLive,
  liveScoreByTeam,
}: {
  rows: StandingWithTeam[];
  highlightTeamIds?: string[];
  /** Provisional table folding in a live result: shows a "live" banner + arrows. */
  isLive?: boolean;
  /** Per-team live goals (team's perspective) — shows a coloured score chip on its row. */
  liveScoreByTeam?: Map<string, { gf: number; ga: number }>;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [mode, setMode] = useState<TableMode>('short');
  const [venue, setVenue] = useState<Venue>('overall');
  const numKeys = MODE_NUMS[mode];
  const showForm = mode === 'form';
  const width = tableWidth(numKeys, showForm);
  const hasVenueSplits = useMemo(() => rows.some((r) => r.home || r.away), [rows]);
  const displayRows = useMemo(() => applyVenue(rows, venue), [rows, venue]);
  const groups = useMemo(() => {
    const map = new Map<string, StandingWithTeam[]>();
    for (const row of displayRows) {
      const key = row.group_label ?? '';
      const bucket = map.get(key);
      if (bucket) {
        bucket.push(row);
      } else {
        map.set(key, [row]);
      }
    }
    return [...map.entries()];
  }, [displayRows]);
  const hasGroups = displayRows.some((row) => Boolean(row.group_label));
  const cycleVenue = () => setVenue(VENUES[(VENUES.indexOf(venue) + 1) % VENUES.length]);

  return (
    <View className="gap-4">
      <View className="gap-2 px-1">
        {isLive ? (
          <View className="flex-row items-center gap-2">
            <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: palette.volt }} />
            <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt-bright">
              {t('leagues.liveStandings')}
            </AppText>
          </View>
        ) : null}
        <View className="flex-row items-center justify-between gap-2">
          {hasVenueSplits ? (
            <Pressable
              onPress={cycleVenue}
              accessibilityRole="button"
              className="flex-row items-center gap-1 rounded-full border border-line bg-surface px-3 py-1.5 active:opacity-80"
            >
              <AppText variant="micro" weight="bold" className="text-ink">
                {t(`leagues.venue_${venue}`)}
              </AppText>
              <Ionicons name="chevron-down" size={12} color={palette.muted} />
            </Pressable>
          ) : (
            <View />
          )}
          <View style={{ flexShrink: 1 }}>
            <Segmented
              options={[
                { value: 'short', label: t('leagues.modeShort') },
                { value: 'full', label: t('leagues.modeFull') },
                { value: 'form', label: t('leagues.modeForm') },
              ]}
              value={mode}
              onChange={setMode}
            />
          </View>
        </View>
      </View>
      {groups.map(([label, groupRows]) => (
        <Card key={label || 'main'} className="overflow-hidden">
          {label ? (
            <View className="border-b border-line bg-surface-2/50 px-3 py-2">
              <AppText variant="micro" weight="semibold" className="text-volt">
                {locale === 'ar'
                  ? localizeRound(label, locale)
                  : t('leagues.group', { name: label.replace(/^group\s*/i, '') })}
              </AppText>
            </View>
          ) : null}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingHorizontal: 12, minWidth: '100%' }}
          >
            <View style={{ width }}>
              <HeaderRow numKeys={numKeys} showForm={showForm} />
              {groupRows.map((row, index) => (
                <View key={row.id} className={index > 0 ? 'border-t border-line/50' : ''}>
                  <StandingRowView
                    row={row}
                    highlight={Boolean(row.team && highlightTeamIds?.includes(row.team.id))}
                    zone={zoneFor(row.rank, groupRows.length, Boolean(label))}
                    liveScore={
                      venue === 'overall' && row.team
                        ? liveScoreByTeam?.get(row.team.id)
                        : undefined
                    }
                    numKeys={numKeys}
                    showForm={showForm}
                  />
                </View>
              ))}
            </View>
          </ScrollView>
        </Card>
      ))}

      {rows.length > 0 ? (
        <View className="flex-row flex-wrap gap-4 px-1">
          <LegendDot tone="bg-win" label={t('leagues.zoneQualify')} />
          {!hasGroups && rows.length > 4 ? (
            <LegendDot tone="bg-loss" label={t('leagues.zoneRelegation')} />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
